/**
 * Geographic Verification Service
 * Handles distance calculations and proximity checks for SMF and Sacramento
 */

const SMF_COORDS = { lat: 38.6951, lng: -121.5908 }; // 6900 Airport Blvd
const SAC_CENTER_COORDS = { lat: 38.5816, lng: -121.4944 };

/**
 * Calculates distance between two points using Haversine formula (miles)
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Radius of Earth in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Gets current position with high accuracy
 */
export const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });
};

/**
 * Checks if user is within a specific radius of SMF
 */
export const verifySmfProximity = async (maxMiles = 10) => {
  try {
    const pos = await getCurrentPosition();
    const dist = calculateDistance(
      pos.coords.latitude,
      pos.coords.longitude,
      SMF_COORDS.lat,
      SMF_COORDS.lng
    );
    return { verified: dist <= maxMiles, distance: dist };
  } catch (err) {
    throw new Error("Location access is required to verify your report integrity.");
  }
};

/**
 * Checks if user is in the Sacramento Region (30 mile radius)
 */
export const verifySacramentoRegion = async (maxMiles = 30) => {
  try {
    const pos = await getCurrentPosition();
    const dist = calculateDistance(
      pos.coords.latitude,
      pos.coords.longitude,
      SAC_CENTER_COORDS.lat,
      SAC_CENTER_COORDS.lng
    );
    return { verified: dist <= maxMiles, distance: dist };
  } catch (err) {
    throw new Error("Location access required to activate sharing in Sacramento.");
  }
};